'use client'

import { usePathname } from 'next/navigation'
import { Suspense, useEffect } from 'react'

import { QueryProvider } from '@/lib/query'
import { ToastProvider } from '@/context/ToastContext'
import { ThemeProvider } from '@/context/ThemeContext'
import { AuthProvider } from '@/context/AuthContext'
import { AIProvider } from '@/context/AIContext'
import Layout from '@/components/Layout'
import LayoutV2 from '@/components/LayoutV2'
import { NewVersionBanner } from '@/components/NewVersionBanner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useTheme } from '@/context/ThemeContext'

function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isSetupRoute = pathname === '/setup'
  const isLabsRoute = pathname === '/labs' || pathname.startsWith('/labs/')
  const shouldUseAppShell = !isSetupRoute && !isLabsRoute

  const { layoutVersion } = useTheme()

  if (!shouldUseAppShell) return <>{children}</>

  if (layoutVersion === 'v2') {
    return (
      <Suspense fallback={null}>
        <LayoutV2>{children}</LayoutV2>
      </Suspense>
    )
  }

  return (
    <>
      <NewVersionBanner />
      <Layout>{children}</Layout>
    </>
  )
}

export default function ProtectedShell({
    children,
}: {
    children: React.ReactNode
}) {
    const pathname = usePathname()

    // #region agent log
    useEffect(() => {
      if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
        const detectEnv = async () => {
          const env = {
            userAgent: navigator.userAgent,
            isCursorBrowser: navigator.userAgent.includes('Cursor') || window.location.hostname === 'localhost',
            hasServiceWorker: 'serviceWorker' in navigator,
            serviceWorkerReady: false,
            cacheAvailable: 'caches' in window,
            devToolsOpen: false,
            reactDevTools: !!(window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__,
            localStorageAvailable: typeof Storage !== 'undefined',
            sessionStorageAvailable: typeof sessionStorage !== 'undefined',
          };
          if (env.hasServiceWorker) {
            try {
              const registration = await navigator.serviceWorker.getRegistration();
              env.serviceWorkerReady = !!registration;
            } catch {}
          }
          let devtools = false;
          const threshold = 160;
          devtools = (window.outerWidth - window.innerWidth > threshold) || (window.outerHeight - window.innerHeight > threshold);
          env.devToolsOpen = devtools;
        };
        detectEnv();
      }
    }, [pathname]);
    // #endregion

    return (
        <QueryProvider>
            <ToastProvider>
                <ThemeProvider>
                    <AuthProvider>
                        <AIProvider>
                            <TooltipProvider delayDuration={200}>
                                <AppShell>{children}</AppShell>
                            </TooltipProvider>
                        </AIProvider>
                    </AuthProvider>
                </ThemeProvider>
            </ToastProvider>
        </QueryProvider>
    )
}
